import sanitize from 'sanitize-html';

export const isAllowedImageType = (img: string): boolean => IMAGE_TYPES.includes((img.split('.').pop() || '').toLowerCase());

export const isNotDuplicatedExtension = (img: string): boolean => {
  return img.split('.').length <= 2;
};

export const isAllowedModelType = (file: File): boolean => MODEL_TYPES.includes((file.name.split('.').pop() || '').toLowerCase());

export const isAllowedSize = (img: File): boolean => img.size > 0 && toMBytes(img.size) < 32;

export const toMBytes = (sizeInBytes: number): number => sizeInBytes / 1024 / 1024;

const IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'bmp'];

const MODEL_TYPES = ['zip'];

interface IShape {
  width: number;
  height: number;
}

const THUMBNAIL_SIZE = { width: 270, height: 200 };

export async function generateThumbnail(file: File | Blob): Promise<Blob> {
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const { width, height } = resizeMax(image, THUMBNAIL_SIZE);
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
  return exportCanvas(canvas);
}

async function loadImage(file: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.src = sanitize(url);
    image.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve(image);
    });
    image.addEventListener('error', (event: ErrorEvent) => {
      URL.revokeObjectURL(url);
      reject((event.error as Error) || new Error('Could not create thumbnail'));
    });
  });
}

export function resizeMax(original: IShape, desired: IShape): IShape {
  const originalAspectRatio = original.width / original.height;
  const thumbnailAspectRatio = desired.width / desired.height;

  if (thumbnailAspectRatio < originalAspectRatio) {
    return {
      width: desired.width,
      height: Math.round(desired.width / originalAspectRatio),
    };
  }
  return {
    height: desired.height,
    width: Math.round(desired.height * originalAspectRatio),
  };
}

async function exportCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Thumbnail generation error.'));
      }
    }, 'image/jpeg');
  });
}

export async function stripExifTags(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = (event: ProgressEvent<FileReader>): void => {
      resolve(removeExifTags(event.target?.result as ArrayBuffer));
    };
    fr.onerror = (): void => {
      reject(new Error('Could not strip exif tags'));
    };
    fr.readAsArrayBuffer(file);
  });
}

type Piece = { recess: number; offset: number };

function removeExifTags(buffer: ArrayBuffer): Blob {
  const dv = new DataView(buffer);
  let offset = 0;
  let recess = 0;
  const pieces: Piece[] = [];
  let i = 0;
  if (dv.getUint16(offset) === 0xffd8) {
    offset += 2;
    let app1 = dv.getUint16(offset);
    offset += 2;
    while (offset < dv.byteLength) {
      if (app1 === 0xffe1) {
        pieces[i] = { recess, offset: offset - 2 };
        recess = offset + dv.getUint16(offset);
        i += 1;
      } else if (app1 === 0xffda) {
        break;
      }
      offset += dv.getUint16(offset);
      app1 = dv.getUint16(offset);
      offset += 2;
    }
    if (pieces.length > 0) {
      const newPieces: BlobPart[] = [];
      pieces.forEach((v) => {
        newPieces.push(buffer.slice(v.recess, v.offset));
      });
      newPieces.push(buffer.slice(recess));
      return new Blob(newPieces);
    }
  }
  return new Blob([buffer]);
}
